import { useState } from "react";
import { Col, CardTitle, Card, Button, Row } from "reactstrap";
import {
  RemoveAplPeriod,
  UpdateAplPeriodDates,
} from "../../actions/teacherMutations";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { FormattedMessage } from "react-intl";
import { toast } from "react-toastify";
import ConfirmDialog from "../Modal/ConfirmDialog";
import ChangeAPLPeriodDateModal from "../Modal/ChangeAPLPeriodDateModal";
import { isValid, parseISO } from "date-fns";
import { dateIsAfter, formatDate } from "../../Utilities/dateUtils";

export default function RemoveAndChangeDateApl({
  periodId,
  periodName,
  currentStartDate,
  currentEndDate,
  level,
  objId,
  setPeriod,
}) {
  const mutateRemoveAplPeriod = useMutation({ mutationFn: RemoveAplPeriod });
  const mutateUpdateAplPeriod = useMutation({
    mutationFn: UpdateAplPeriodDates,
  });

  const [promptForDelete, setPromptForDelete] = useState(false);
  const [promptForEnd, setPromptForEnd] = useState(false);
  const [promptForChangeDates, setPromptForChangeDates] = useState(false);
  const queryClient = useQueryClient();

  const parsedDate = (date) =>
    date && date !== "" && isValid(parseISO(date))
      ? parseISO(date)
      : new Date();

  const [newStartDate, setNewStartDate] = useState(
    parsedDate(currentStartDate)
  );
  const [newEndDate, setNewEndDate] = useState(parsedDate(currentEndDate));

  const handleEndPeriod = () => {
    if (dateIsAfter(currentEndDate)) {
      mutateUpdateAplPeriod.mutate(
        {
          periodId: periodId,
          startDate: currentStartDate,
          endDate: formatDate(new Date(), "yyyy-MM-dd'T'HH:mm:ss"),
        },
        {
          onSuccess: (data) => {
            if (data?.status > 199 && data.status < 300) {
              setPromptForEnd(false);
              toast.success(<FormattedMessage id="PeriodEnded" />);
              queryClient.invalidateQueries({ queryKey: ["FetchPeriods", [level, objId]] });
            } else {
              toast.warn(data);
            }
          },
          onError: (data) => {
            setPromptForEnd(false);
            toast.warn(data.response.data);
          },
        }
      );
    } else {
      setPromptForEnd(false);
      toast.warn(<FormattedMessage id="EndDateForPeriodAlreadyPassed" />);
    }
  };

  const handleDeletePeriod = () => {
    mutateRemoveAplPeriod.mutate(
      { periodId: periodId },
      {
        onSuccess: (data) => {
          if (data?.status > 199 && data.status < 300) {
            setPromptForDelete(false);
            toast.success(<FormattedMessage id="PeriodDeleted" />);
            setPeriod(null);
            queryClient.invalidateQueries({ queryKey: ["FetchPeriods", [level, objId]] });
          } else {
            toast.warn(data ? data : "");
          }
        },
        onError: (data) => {
          setPromptForEnd(false);
          toast.warn(data.response.data);
        },
      }
    );
  };

  const handleChangeStartOrEndDates = (startDate, endDate) => {
    mutateUpdateAplPeriod.mutate(
      {
        periodId: periodId,
        startDate: formatDate(startDate, "yyyy-MM-dd'T'HH:mm:ss"),
        endDate: formatDate(endDate, "yyyy-MM-dd'T'HH:mm:ss"),
      },
      {
        onSuccess: (data) => {
          if (data?.status > 199 && data.status < 300) {
            setPromptForChangeDates(false);
            toast.success(<FormattedMessage id="DatesChanged" />);
            queryClient.invalidateQueries({ queryKey: ["FetchPeriods", [level, objId]] });
          } else {
            toast.warn(data ? data : "");
            setNewStartDate(parsedDate(currentStartDate));
            setNewEndDate(parsedDate(currentEndDate));
          }
        },
        onError: (data) => {
          setNewStartDate(parsedDate(currentStartDate));
          setNewEndDate(parsedDate(currentEndDate));
          toast.warn(data.response.data);
        },
      }
    );
  };

  return (
    <Col lg={12}>
      <Card body>
        <CardTitle className="mb-0">
          <FormattedMessage id="Edit" /> - {periodName}
          <hr />
        </CardTitle>
        <Row>
          <Col lg={6}>
            <Button
              color="primary"
              className="mx-1"
              outline
              onClick={() => setPromptForDelete(true)}
            >
              <FormattedMessage id="RemovePeriod" />
            </Button>
            <Button
              color="primary mx-1"
              className="mx-1"
              outline
              onClick={() => setPromptForEnd(true)}
            >
              <FormattedMessage id="EndPeriod" />
            </Button>
            <Button
              color="primary mx-1"
              className="mx-1"
              outline
              onClick={() => setPromptForChangeDates(true)}
            >
              <FormattedMessage id="ChangeDates" />
            </Button>
          </Col>
        </Row>
      </Card>
      <ConfirmDialog
        danger={true}
        isOpen={promptForDelete}
        toggle={() => setPromptForDelete(!promptForDelete)}
        title={<FormattedMessage id="AreYouSure" />}
        message={
          <FormattedMessage
            values={{ periodName: periodName }}
            id="AplPeriodWillBeDeleted"
          />
        }
        onProceed={handleDeletePeriod}
      />
      <ConfirmDialog
        isOpen={promptForEnd}
        toggle={() => setPromptForEnd(!promptForEnd)}
        title={<FormattedMessage id="AreYouSure" />}
        message={
          <FormattedMessage
            values={{ periodName: periodName }}
            id="AplPeriodWillEnd"
          />
        }
        onProceed={handleEndPeriod}
        danger={true}
      />
      <ChangeAPLPeriodDateModal
        periodName={periodName}
        currentStartDate={currentStartDate}
        currentEndDate={currentEndDate}
        isOpen={promptForChangeDates}
        toggle={() => setPromptForChangeDates(!promptForChangeDates)}
        newStartDate={newStartDate}
        newEndDate={newEndDate}
        setNewStartDate={setNewStartDate}
        setNewEndDate={setNewEndDate}
        handleChangeStartOrEndDates={handleChangeStartOrEndDates}
      />
    </Col>
  );
}
